import React, { useState, useEffect, useCallback } from 'react';
import {
    Notifications,
    Refresh,
    CheckCircle,
    FilterList,
    Delete,
    DoneAll
} from '@mui/icons-material';
import AdminSidebar from "../../components/admin/AdminSidebar";
import DashboardHeader from "../../components/admin/DashboardHeader";
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { fetchNotifications, markAllNotificationsAsRead, markNotificationAsRead } from '../../services/notificationService';
import { formatNotificationTitle, formatNotificationMessage } from '../../utils/notificationFormatter';

const NotificationsPage = () => {
    const { user, selectedOrganization } = useAuth();
    const { unViewed, setUnViewed, fetchUnViewedCount } = useNotification();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pge, setPge] = useState(1);
    const [lmt] = useState(20);
    const [nextPage, setNextPage] = useState(1);
    const [prevPage, setPrevPage] = useState(1);
    const [sts, setSts] = useState('all');
    const [vzn, setVzn] = useState('all');
    const [chn, setChn] = useState('all');
    const [slg, setSlg] = useState('');

    // Déterminer ntd selon le rôle (comme sur mobile)
    const ntd = selectedOrganization?.role === 'admin' 
        ? selectedOrganization?.id 
        : user?.id;

    const loadNotifications = useCallback(async () => {
        if (!ntd || !user?.id || !selectedOrganization?.id) return;

        try {
            setLoading(true);
            const params = {
                pge,
                lmt,
                ntd,
                sts,
                vzn,
                chn,
                slg
            };

            const data = await fetchNotifications(params);
            
            // Si data est un objet avec contents, utiliser contents, sinon utiliser data directement
            const notificationsList = data?.contents || (Array.isArray(data) ? data : []);
            
            // Filtrer les notifications pay-pmt-rqst comme sur mobile
            const filtered = notificationsList.filter(n => n.code !== 'pay-pmt-rqst');
            
            setNotifications(filtered);
            
            if (data?.nextPage) setNextPage(data.nextPage);
            if (data?.previousPage) setPrevPage(data.previousPage);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [pge, lmt, ntd, sts, vzn, chn, slg, user?.id, selectedOrganization?.id]);

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    const handleRefresh = () => {
        setPge(1);
        setPrevPage(1);
        setNextPage(1);
        loadNotifications();
    };

    const handleMarkAllAsRead = async () => {
        if (!ntd || notifications.length === 0) return;

        try {
            setSubmitting(true);
            const unListed = notifications.filter(n => n.status === 'received');
            
            if (unListed.length === 0) return;

            const ids = unListed.map(n => n.id).join(',');
            
            await markAllNotificationsAsRead({
                sts: 'viewed',
                ntd,
                ids
            });

            // Mettre à jour localement
            const updated = notifications.map(n => 
                unListed.some(u => u.id === n.id) 
                    ? { ...n, status: 'viewed' }
                    : n
            );
            setNotifications(updated);
            
            // Rafraîchir le compteur
            if (fetchUnViewedCount) {
                await fetchUnViewedCount();
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        if (!ntd) return;

        try {
            await markNotificationAsRead(notificationId, ntd, 'viewed');
            
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, status: 'viewed' } : n)
            );
            
            // Rafraîchir le compteur
            if (fetchUnViewedCount) {
                await fetchUnViewedCount();
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const formatDateTime = (date, time) => {
        if (!date) return 'Date inconnue';
        const dateTime = time ? new Date(`${date}T${time}`) : new Date(date);
        const now = new Date();
        const diff = now - dateTime;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'À l\'instant';
        if (minutes < 60) return `Il y a ${minutes} min`;
        if (hours < 24) return `Il y a ${hours} h`;
        if (days < 7) return `Il y a ${days} j`;
        
        return dateTime.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getNotificationIcon = (template) => {
        switch (template) {
            case 'PaymentCashedItem':
                return <CheckCircle className="text-green-500" />;
            case 'PaymentReceivedItem':
                return <CheckCircle className="text-blue-500" />;
            case 'PaymentRejectedItem':
                return <Delete className="text-red-500" />;
            default:
                return <Notifications className="text-amber-500" />;
        }
    };

    const getNotificationColor = (status) => {
        switch (status) {
            case 'received':
                return 'bg-amber-50 border-l-amber-500';
            case 'viewed':
                return 'bg-gray-50 border-l-gray-300';
            case 'listed':
                return 'bg-blue-50 border-l-blue-300';
            default:
                return 'bg-white border-l-gray-200';
        }
    };

    return (
        <div className="min-h-screen bg-white flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="notifications"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 via-white to-amber-50/30 min-h-screen py-6 pr-6 pl-0">
                    <div className="relative z-10 space-y-6 pl-6">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                            currency={currency}
                            onCurrencyChange={setCurrency}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />

                        {/* En-tête de la page — charte amber */}
                        <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 rounded-3xl p-8 text-white shadow-xl shadow-amber-900/10">
                            <div className="absolute inset-0 bg-black/5" />
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
                            <div className="relative z-10 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-white/20">
                                        <Notifications className="text-white text-2xl" />
                                    </div>
                                    <div>
                                        <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
                                        <p className="text-amber-100 text-sm mt-0.5">Activité et alertes sans données sensibles</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    className="p-3 bg-white/20 backdrop-blur rounded-xl hover:bg-white/30 border border-white/20 transition-all duration-200"
                                >
                                    <Refresh className={`text-white text-xl ${loading ? 'animate-spin' : ''}`} />
                                </button>
                            </div>
                        </div>

                        {/* Filtres et statistiques */}
                        <div className="bg-white rounded-2xl p-5 border border-amber-100 shadow-md">
                            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                                <div className="flex items-center gap-3">
                                    <span className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
                                        <FilterList className="text-amber-600 text-lg" />
                                    </span>
                                    <h2 className="text-base font-semibold text-gray-900">Filtres</h2>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                                    <span className="text-sm font-medium text-amber-800">Non lues</span>
                                    <span className="px-2.5 py-0.5 bg-amber-500 text-white rounded-lg text-sm font-bold">
                                        {unViewed || 0}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <select
                                    value={sts}
                                    onChange={(e) => setSts(e.target.value)}
                                    className="px-4 py-2.5 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 bg-white"
                                >
                                    <option value="all">Tous les statuts</option>
                                    <option value="received">Reçues</option>
                                    <option value="listed">Listées</option>
                                    <option value="viewed">Vues</option>
                                </select>
                                <select
                                    value={vzn}
                                    onChange={(e) => setVzn(e.target.value)}
                                    className="px-4 py-2.5 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 bg-white"
                                >
                                    <option value="all">Toutes les visibilités</option>
                                    <option value="push">Push</option>
                                    <option value="dropdown">Dropdown</option>
                                </select>
                                <select
                                    value={chn}
                                    onChange={(e) => setChn(e.target.value)}
                                    className="px-4 py-2.5 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 bg-white"
                                >
                                    <option value="all">Tous les canaux</option>
                                    <option value="socket">Socket</option>
                                    <option value="push">Push</option>
                                </select>
                            </div>
                        </div>

                        {/* Liste des notifications — affichage sécurisé (sans données sensibles) */}
                        <div className="bg-white rounded-2xl p-6 border border-amber-100 shadow-md">
                            {loading && notifications.length === 0 ? (
                                <div className="text-center py-16">
                                    <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                    <p className="text-gray-600">Chargement des notifications...</p>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="text-center py-16">
                                    <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
                                        <Notifications className="text-amber-400 text-4xl" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-700 mb-1">Aucune notification</h3>
                                    <p className="text-gray-500 text-sm">Vous êtes à jour.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {notifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            className={`rounded-2xl border-l-4 p-5 transition-all duration-200 hover:shadow-md ${getNotificationColor(notification.status)}`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/80 flex items-center justify-center shadow-sm">
                                                    {getNotificationIcon(notification.template)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className={`font-semibold text-base mb-1 ${
                                                                notification.status === 'received' ? 'text-gray-900' : 'text-gray-700'
                                                            }`}>
                                                                {formatNotificationTitle(notification)}
                                                            </h4>
                                                            <p className={`text-sm mb-2 ${
                                                                notification.status === 'received' ? 'text-gray-700' : 'text-gray-600'
                                                            }`}>
                                                                {formatNotificationMessage(notification) || 'Nouvelle activité.'}
                                                            </p>
                                                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                                                <span>{formatDateTime(notification.date, notification.time)}</span>
                                                                {notification.code && notification.code !== 'pay-pmt-rqst' && (
                                                                    <span className="px-2 py-0.5 bg-gray-100 rounded-md font-medium text-gray-600">
                                                                        {notification.code}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {notification.status === 'received' && (
                                                            <button
                                                                onClick={() => handleMarkAsRead(notification.id)}
                                                                className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                                                title="Marquer comme lu"
                                                            >
                                                                <CheckCircle className="text-xl" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Pagination */}
                            {notifications.length > 0 && (
                                <div className="flex items-center justify-between mt-6 pt-6 border-t border-amber-100">
                                    <button
                                        onClick={() => setPge(prevPage)}
                                        disabled={prevPage >= pge || loading}
                                        className="px-4 py-2.5 bg-amber-50 text-amber-800 rounded-xl hover:bg-amber-100 border border-amber-200 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Précédent
                                    </button>
                                    <span className="text-sm text-gray-600 font-medium">Page {pge}</span>
                                    <button
                                        onClick={() => setPge(nextPage)}
                                        disabled={nextPage <= pge || loading}
                                        className="px-4 py-2.5 bg-amber-50 text-amber-800 rounded-xl hover:bg-amber-100 border border-amber-200 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Suivant
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        {notifications.length > 0 && (
                            <div className="bg-amber-50/80 rounded-2xl p-5 border border-amber-100 shadow-md">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div>
                                        <h3 className="text-base font-semibold text-gray-900 mb-0.5">Tout marquer comme lu</h3>
                                        <p className="text-sm text-gray-600">Marquer toutes les notifications comme lues</p>
                                    </div>
                                    <button
                                        onClick={handleMarkAllAsRead}
                                        disabled={submitting || notifications.filter(n => n.status === 'received').length === 0}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                <span>En cours...</span>
                                            </>
                                        ) : (
                                            <>
                                                <DoneAll className="text-lg" />
                                                <span>Marquer tout comme lu</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationsPage;










